import React, { useEffect, useMemo } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useNFe, useAtualizarInformacoesAdicionais } from '../../../../hooks/useNFe';
import { AtualizarInformacoesAdicionaisRequest } from '../../../../types/nfe';

interface SecaoInformacoesAdicionaisProps {
  nfeId: string;
  isReadOnly: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const SecaoInformacoesAdicionais: React.FC<SecaoInformacoesAdicionaisProps> = ({
  nfeId,
  isReadOnly,
  onDirtyChange,
}) => {
  const { data: nfe } = useNFe(nfeId);
  const informacoesAdicionais = useMemo(
    () => nfe
      ? { informacoesComplementares: nfe.informacoesComplementares, informacoesFisco: nfe.informacoesFisco }
      : undefined,
    [nfe],
  );
  const { showToast } = useToast();
  const mutation = useAtualizarInformacoesAdicionais(nfeId);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<AtualizarInformacoesAdicionaisRequest>({
    defaultValues: informacoesAdicionais || {
      informacoesComplementares: '',
      informacoesFisco: '',
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (informacoesAdicionais && !isDirty) {
      reset(informacoesAdicionais);
    }
  }, [informacoesAdicionais, reset, isDirty]);

  const onSubmit = async (data: AtualizarInformacoesAdicionaisRequest) => {
    try {
      await mutation.mutateAsync(data);
      showToast('Informações adicionais atualizadas com sucesso', 'success');
      reset(data);
    } catch (error: any) {
      console.error('Erro ao atualizar informações adicionais:', error);
      // Error handled by API interceptor
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {mutation.isError && (
        <Alert
          variant="danger"
          className="border border-danger border-opacity-25 bg-danger-subtle mb-3"
        >
          <div className="text-danger-emphasis">
            Erro ao atualizar informações adicionais. Por favor, tente novamente.
          </div>
        </Alert>
      )}

      <Row className="g-3">
        <Col xs={12}>
          <p className="text-body-tertiary small mb-3">
            Campos opcionais para adicionar informações complementares e observações fiscais à NFe.
          </p>
        </Col>

        {/* Informações Complementares */}
        <Col xs={12}>
          <Form.Group>
            <Form.Label className="text-body-secondary fw-semibold">
              Informações Complementares
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              {...register('informacoesComplementares')}
              disabled={isReadOnly}
              placeholder="Informações complementares de interesse do contribuinte (ex: dados adicionais do produto, condições de pagamento, etc.)"
              maxLength={5000}
            />
            <Form.Text className="text-body-tertiary">
              Máximo 5000 caracteres. Aparece no DANFE.
            </Form.Text>
          </Form.Group>
        </Col>

        {/* Informações Fiscais */}
        <Col xs={12}>
          <Form.Group>
            <Form.Label className="text-body-secondary fw-semibold">
              Informações ao Fisco
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('informacoesFisco')}
              disabled={isReadOnly}
              placeholder="Informações de interesse do fisco (ex: observações sobre regime tributário, benefícios fiscais, etc.)"
              maxLength={2000}
            />
            <Form.Text className="text-body-tertiary">
              Máximo 2000 caracteres. Uso exclusivo do fisco.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {/* Action Buttons */}
      {!isReadOnly && (
        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top border-opacity-25">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!isDirty || mutation.isPending}
            loading={mutation.isPending}
            loadingPosition="start"
          >
            {mutation.isPending ? 'Salvando...' : 'Salvar Informações'}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default SecaoInformacoesAdicionais;
